import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';

/**
 * Themed horizontal ranked-bar chart (ApexCharts) — replaces the legacy
 * Google Charts BarChart used by Top 5 Tenants / Categories.
 *
 * props:
 *   items   : [{ label, value }]   already in display units (e.g. millions)
 *   suffix  : string appended in datalabels/tooltip (e.g. 'M', '%')
 *   height  : px (default 360)
 *
 * Axis/grid/tooltip colors come from admin-dashboard.css (.apexcharts-*),
 * so they follow the active theme; only the bar palette is set here (SVG
 * fills need literal colors, not CSS vars).
 */

// V3 accent palette — distributed across the (≤5) bars.
const PALETTE = ['#3B82F6', '#8B5CF6', '#06B6D4', '#10B981', '#F59E0B'];

const RankedBarChart = ({ items = [], suffix = '', height = 360 }) => {
    const { t } = useTranslation();
    const { categories, data, colors } = useMemo(() => {
        // Ascending so the largest bar renders at the TOP in a horizontal chart.
        const sorted = [...items].sort((a, b) => a.value - b.value);
        return {
            categories: sorted.map((i) => i.label),
            data: sorted.map((i) => Number(Number(i.value).toFixed(2))),
            // Brightest accent for the largest (last) bar.
            colors: sorted.map((_, i) => PALETTE[(PALETTE.length - 1 - (sorted.length - 1 - i)) % PALETTE.length]),
        };
    }, [items]);

    const options = {
        chart: {
            type: 'bar',
            height,
            toolbar: { show: false },
            fontFamily: 'inherit',
            background: 'transparent',
            animations: { speed: 400 },
        },
        plotOptions: {
            bar: {
                horizontal: true,
                borderRadius: 6,
                borderRadiusApplication: 'end',
                barHeight: '60%',
                distributed: true,
            },
        },
        colors,
        dataLabels: {
            enabled: true,
            formatter: (val) => `${val}${suffix}`,
            offsetX: 6,
            textAnchor: 'start',
            style: { fontSize: '12px', fontWeight: 600, colors: ['#fff'] },
            dropShadow: { enabled: false },
        },
        grid: { strokeDashArray: 4, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
        xaxis: {
            categories,
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: { formatter: (v) => `${Number(v).toFixed(0)}${suffix}` },
        },
        yaxis: { labels: { style: { fontSize: '12.5px' } } },
        legend: { show: false },
        tooltip: {
            y: { formatter: (val) => `${val}${suffix}` },
        },
        states: { hover: { filter: { type: 'lighten', value: 0.08 } } },
    };

    return <Chart options={options} series={[{ name: t('Value'), data }]} type="bar" height={height} />;
};

export default RankedBarChart;
